/**
 * Storefront Page Builder Widget
 *
 * Point-and-click editor for page sections (hero, product-grid, etc.).
 * Edits sections_json field and provides publish functionality.
 */

import { useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/i18n/useLanguage';
import type { ExperienceWidgetProps } from '@/lib/views/widgets/registry';
import type { StorefrontPageConfigDoc, PageSection, PageConfigV2, OverlayElement } from '../types';
import { getSectionType } from './sectionRegistry';
import { SectionEditor } from './SectionEditor';
import { OverlayEditor } from './OverlayEditor';
import { OverlayCanvas } from './OverlayCanvas';
import { usePublishStorefrontPageConfig } from '../api/storefrontPageConfigHooks';
import { ChevronUp, ChevronDown, Trash2, Plus, AlertCircle } from 'lucide-react';
import { useCanPublishQuery } from '@/hooks/useCanPublishQuery';

export function StorefrontPageBuilderWidget({
  formData,
  onFieldChange,
  disabled,
}: ExperienceWidgetProps) {
  const { language } = useLanguage();
  const publishPageConfig = usePublishStorefrontPageConfig();

  // Cast formData to typed doc
  const doc = formData as StorefrontPageConfigDoc;
  const isNew = !doc.name || doc.name === 'new';

  const { allowed: canPublish } = useCanPublishQuery(
    "Storefront Page Config",
    !isNew ? doc.name : undefined
  );

  // Parse sections_json - supports v1 (array) or v2 (object)
  const { sections, overlays } = useMemo(() => {
    try {
      const data = JSON.parse(doc.sections_json || '[]');
      if (Array.isArray(data)) {
        // v1 format: array of sections
        return { sections: data as PageSection[], overlays: [] };
      } else if (data && typeof data === 'object' && data.version === 'v2') {
        // v2 format: object with sections and overlays
        return {
          sections: (data.sections || []) as PageSection[],
          overlays: (data.overlays || []) as OverlayElement[],
        };
      }
      return { sections: [], overlays: [] };
    } catch {
      return { sections: [], overlays: [] };
    }
  }, [doc.sections_json]);

  // Section editor state
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(null);
  const [editingSection, setEditingSection] = useState<PageSection | null>(null);
  const [isAddingSection, setIsAddingSection] = useState(false);

  // Overlay editor state
  const [editingOverlayIndex, setEditingOverlayIndex] = useState<number | null>(null);
  const [isAddingOverlay, setIsAddingOverlay] = useState(false);
  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(null);

  // Update sections array (saves as v2 format)
  const updateSections = useCallback((newSections: PageSection[]) => {
    const v2Config: PageConfigV2 = {
      version: 'v2',
      sections: newSections,
      overlays,
    };
    onFieldChange('sections_json', JSON.stringify(v2Config, null, 2));
  }, [onFieldChange, overlays]);

  // Update overlays array (saves as v2 format)
  const updateOverlays = useCallback((newOverlays: OverlayElement[]) => {
    const v2Config: PageConfigV2 = {
      version: 'v2',
      sections,
      overlays: newOverlays,
    };
    onFieldChange('sections_json', JSON.stringify(v2Config, null, 2));
  }, [onFieldChange, sections]);

  // Add new section
  const handleAddSection = useCallback(() => {
    setEditingSection(null);
    setEditingSectionIndex(null);
    setIsAddingSection(true);
  }, []);

  // Edit existing section
  const handleEditSection = useCallback((index: number) => {
    setEditingSection(sections[index]);
    setEditingSectionIndex(index);
    setIsAddingSection(false);
  }, [sections]);

  // Save section (add or update)
  const handleSaveSection = useCallback((section: PageSection) => {
    if (editingSectionIndex !== null) {
      // Update existing
      const newSections = [...sections];
      newSections[editingSectionIndex] = section;
      updateSections(newSections);
    } else {
      // Add new
      updateSections([...sections, section]);
    }
    setEditingSection(null);
    setEditingSectionIndex(null);
    setIsAddingSection(false);
  }, [sections, editingSectionIndex, updateSections]);

  // Delete section
  const handleDeleteSection = useCallback((index: number) => {
    if (confirm('Are you sure you want to delete this section?')) {
      const newSections = sections.filter((_, i) => i !== index);
      updateSections(newSections);
    }
  }, [sections, updateSections]);

  // Move section up
  const handleMoveUp = useCallback((index: number) => {
    if (index === 0) return;
    const newSections = [...sections];
    [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
    updateSections(newSections);
  }, [sections, updateSections]);

  // Move section down
  const handleMoveDown = useCallback((index: number) => {
    if (index === sections.length - 1) return;
    const newSections = [...sections];
    [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
    updateSections(newSections);
  }, [sections, updateSections]);

  // Overlay handlers
  const handleAddOverlay = useCallback(() => {
    setIsAddingOverlay(true);
    setEditingOverlayIndex(null);
    // New overlay will be selected after save (handled in handleSaveOverlay)
  }, []);

  const handleDeleteOverlay = useCallback((index: number) => {
    if (confirm('Are you sure you want to delete this overlay?')) {
      const newOverlays = overlays.filter((_, i) => i !== index);
      updateOverlays(newOverlays);
    }
  }, [overlays, updateOverlays]);

  const handleSaveOverlay = useCallback((overlay: OverlayElement) => {
    if (editingOverlayIndex !== null) {
      const newOverlays = [...overlays];
      newOverlays[editingOverlayIndex] = overlay;
      updateOverlays(newOverlays);
      setSelectedOverlayId(overlay.id);
    } else {
      const newOverlays = [...overlays, overlay];
      updateOverlays(newOverlays);
      setSelectedOverlayId(overlay.id);
    }
    setIsAddingOverlay(false);
    setEditingOverlayIndex(null);
  }, [overlays, editingOverlayIndex, updateOverlays]);

  const handleCanvasUpdate = useCallback((id: string, transform: OverlayElement['transform']) => {
    const index = overlays.findIndex((o) => o.id === id);
    if (index !== -1) {
      const newOverlays = [...overlays];
      newOverlays[index] = { ...newOverlays[index], transform };
      updateOverlays(newOverlays);
    }
  }, [overlays, updateOverlays]);

  const handleCanvasDelete = useCallback(
    (id: string) => {
      const index = overlays.findIndex((o) => o.id === id);
      if (index !== -1) {
        // Skip confirmation for canvas keyboard delete (faster UX)
        const newOverlays = overlays.filter((_, i) => i !== index);
        updateOverlays(newOverlays);
        if (selectedOverlayId === id) {
          setSelectedOverlayId(null);
        }
      }
    },
    [overlays, selectedOverlayId, updateOverlays]
  );

  const handlePublish = async () => {
    if (!doc.name || isNew) {
      alert('Please save the page config first before publishing.');
      return;
    }
    if (!canPublish) {
      alert("You don't have permission to publish this page config.");
      return;
    }

    try {
      const result = await publishPageConfig.mutateAsync({ docname: doc.name });
      if (result.success) {
        // Success - detail query will be invalidated automatically
      } else {
        alert(`Publish failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      alert(`Publish failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <div key={language} className="space-y-6">
      {/* Tenant and Page ID Fields */}
      <Card className="rounded-2xl border-0 bg-white dark:bg-gray-900 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Page Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Tenant</Label>
            <Input
              value={doc.tenant || ''}
              onChange={(e) => onFieldChange('tenant', e.target.value)}
              disabled={disabled || !isNew}
              placeholder="Enter tenant identifier"
              className={!isNew ? 'bg-muted' : ''}
            />
            {!isNew && (
              <p className="text-xs text-muted-foreground">
                Tenant cannot be changed after creation
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Page ID</Label>
            <Input
              value={doc.page_id || ''}
              onChange={(e) => onFieldChange('page_id', e.target.value)}
              disabled={disabled || !isNew}
              placeholder="e.g., home, about, products"
              className={!isNew ? 'bg-muted' : ''}
            />
            {!isNew && (
              <p className="text-xs text-muted-foreground">
                Page ID cannot be changed after creation
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* SEO Fields */}
      <Card className="rounded-2xl border-0 bg-white dark:bg-gray-900 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">SEO</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Meta Title</Label>
            <Input
              value={doc.meta_title || ''}
              onChange={(e) => onFieldChange('meta_title', e.target.value)}
              disabled={disabled}
              placeholder="Page title for search engines"
            />
          </div>
          <div className="space-y-2">
            <Label>Meta Description</Label>
            <Input
              value={doc.meta_description || ''}
              onChange={(e) => onFieldChange('meta_description', e.target.value)}
              disabled={disabled}
              placeholder="Page description for search engines"
            />
          </div>
        </CardContent>
      </Card>

      {/* Sections List */}
      <Card className="rounded-2xl border-0 bg-white dark:bg-gray-900 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Sections</CardTitle>
          <Button onClick={handleAddSection} disabled={disabled} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Section
          </Button>
        </CardHeader>
        <CardContent>
          {sections.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No sections yet. Click "Add Section" to get started.
            </div>
          ) : (
            <div className="space-y-3">
              {sections.map((section, index) => {
                const sectionDef = getSectionType(section.type);
                return (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-4 border rounded-sm bg-muted/50"
                  >
                    <div className="flex-1">
                      <div className="font-medium">
                        {sectionDef?.label || section.type}
                      </div>
                      {sectionDef?.description && (
                        <div className="text-xs text-muted-foreground">
                          {sectionDef.description}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleMoveUp(index)}
                        disabled={disabled || index === 0}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleMoveDown(index)}
                        disabled={disabled || index === sections.length - 1}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditSection(index)}
                        disabled={disabled}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteSection(index)}
                        disabled={disabled}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Overlays Canvas */}
      <Card className="rounded-2xl border-0 bg-white dark:bg-gray-900 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Overlays</CardTitle>
          <Button onClick={handleAddOverlay} disabled={disabled} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Overlay
          </Button>
        </CardHeader>
        <CardContent>
          {overlays.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No overlays yet. Click "Add Overlay" to add stickers or graphics.
            </div>
          ) : (
            <OverlayCanvas
              overlays={overlays}
              selectedId={selectedOverlayId}
              onSelect={setSelectedOverlayId}
              onUpdate={handleCanvasUpdate}
              onDelete={handleCanvasDelete}
            />
          )}
        </CardContent>
      </Card>

      {/* Overlays List (fallback for now) */}
      {overlays.length > 0 && (
        <Card className="rounded-2xl border-0 bg-white dark:bg-gray-900 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Overlay List</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {overlays.map((overlay, index) => (
                <div
                  key={overlay.id}
                  className={`flex items-center gap-3 p-4 border rounded-sm bg-muted/50 ${
                    selectedOverlayId === overlay.id ? 'ring-2 ring-blue-500' : ''
                  }`}
                >
                  <div className="flex-1">
                    <div className="font-medium">
                      {overlay.type} overlay
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Position: {overlay.transform.xPct}%, {overlay.transform.yPct}%
                      {overlay.content && ` • ${overlay.content.substring(0, 30)}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingOverlayIndex(index);
                        setIsAddingOverlay(false);
                        setSelectedOverlayId(overlay.id);
                      }}
                      disabled={disabled}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteOverlay(index)}
                      disabled={disabled}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Publish Section */}
      <Card className="rounded-2xl border-0 bg-white dark:bg-gray-900 shadow-sm">
        <CardContent className="py-4 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {doc.last_published_at
              ? `Last published: ${new Date(doc.last_published_at).toLocaleString()}`
              : 'Never published'}
            {doc.last_publish_error && (
              <div className="text-destructive mt-1 text-xs flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Error: {doc.last_publish_error}
              </div>
            )}
          </div>
          <Button
            onClick={handlePublish}
            disabled={disabled || publishPageConfig.isPending || isNew || sections.length === 0 || !canPublish}
          >
            {publishPageConfig.isPending ? 'Publishing...' : 'Publish Page'}
          </Button>
        </CardContent>
      </Card>

      {/* Section Editor Dialog */}
      <SectionEditor
        section={editingSection}
        open={isAddingSection || editingSectionIndex !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditingSection(null);
            setEditingSectionIndex(null);
            setIsAddingSection(false);
          }
        }}
        onSave={handleSaveSection}
      />

      {/* Overlay Editor Dialog */}
      <OverlayEditor
        overlay={editingOverlayIndex !== null ? overlays[editingOverlayIndex] : null}
        open={isAddingOverlay || editingOverlayIndex !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditingOverlayIndex(null);
            setIsAddingOverlay(false);
          }
        }}
        onSave={handleSaveOverlay}
      />
    </div>
  );
}
